import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSpaData } from "../context/SpaDataContext";
import { useBooking } from "../context/BookingContext";
import { Reveal } from "../components/Reveal";
import { waLink, imgUrl } from "../lib/spa";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { Seo } from "../components/Seo";

export default function TherapistDetail() {
  const { slug } = useParams();
  const { t, ml } = useLanguage();
  const { therapists, massages, loading } = useSpaData();
  const { openBooking } = useBooking();
  const [lightbox, setLightbox] = useState(null);
  const touchX = useRef(0);
  const activeThumbRef = useRef(null);

  const therapist = therapists.find((x) => x.slug === slug);
  const galleryLen = therapist?.gallery?.length || 0;

  // Reset lightbox sempre que muda de terapeuta (evita índice fora do array
  // da nova galeria e evita body.overflow="hidden" a persistir entre rotas).
  useEffect(() => {
    setLightbox(null);
  }, [slug]);

  useEffect(() => {
    if (lightbox !== null && activeThumbRef.current) {
      activeThumbRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [lightbox]);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % galleryLen);
      else if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + galleryLen) % galleryLen);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, galleryLen]);

  // Loader visível (evita ecrã totalmente preto durante carregamento)
  if (loading) {
    return (
      <div
        data-testid="therapist-detail-loading"
        className="flex min-h-screen items-center justify-center bg-background pt-28"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-foreground/55">
            {t("common.loading") || "A carregar..."}
          </p>
        </div>
      </div>
    );
  }

  if (!therapist) {
    return (
      <div
        data-testid="therapist-detail-not-found"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground/65"
      >
        <p className="font-display text-2xl text-foreground">
          {t("therapist.notFound") || "Terapeuta não encontrada"}
        </p>
        <Link to="/terapeutas" className="text-gold underline underline-offset-4">
          {t("therapist.back")}
        </Link>
      </div>
    );
  }

  const therapistMassages = Array.isArray(therapist.massages) ? therapist.massages : [];
  const specialties = therapistMassages
    .map((s) => massages.find((m) => m.slug === s))
    .filter(Boolean);

  const idx = therapists.findIndex((x) => x.slug === slug);
  const safeIdx = idx >= 0 ? idx : 0;
  const totalT = therapists.length || 1;
  const prevT = therapists[(safeIdx - 1 + totalT) % totalT] || therapist;
  const nextT = therapists[(safeIdx + 1) % totalT] || therapist;
  const others = therapists.filter((x) => x.slug !== slug);

  // t() pode devolver o próprio path (fallback) se a chave não existir.
  // Proteger contra tentar chamar uma string como função (evita ecrã preto).
  const availabilityFn = t("therapist.availabilityMessage");
  const availabilityText =
    typeof availabilityFn === "function"
      ? availabilityFn(therapist.name)
      : `${availabilityFn} ${therapist.name}`.trim();

  return (
    <div data-testid="therapist-detail-page" className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8 sm:pt-36">
      <Seo
        title={`${therapist.name} | Magnolia Lunar`}
        description={
          ml(therapist.about)
          || [
              therapist.age && `${therapist.age} ${t("therapist.years")}`,
              ml(therapist.nationality),
              ml(therapist.hair),
            ].filter(Boolean).join(" • ")
          || `${therapist.name} — Magnolia Lunar Spa em Lisboa.`
        }
        image={therapist.profile ? undefined : undefined}
      />
      <Link
        data-testid="therapist-back-link"
        to="/terapeutas"
        className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-foreground/55 transition-colors hover:text-gold"
      >
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
        {t("therapist.back")}
      </Link>

      <div className="mt-10 grid gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden rounded-2xl border border-foreground/8">
            <img src={imgUrl(therapist.profile)} alt={therapist.name} className="w-full object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="text-[10px] uppercase tracking-[0.45em] text-gold">Magnolia Lunar</p>
          <h1 className="mt-3 font-display text-5xl font-light text-foreground sm:text-6xl">{therapist.name}</h1>
          {therapist.available_today && (
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-black/50" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-black" />
              </span>
              {t("team.availableToday")}
            </span>
          )}

          <h3 className="mt-10 text-xs uppercase tracking-[0.3em] text-foreground/65">{t("therapist.about")}</h3>

          {/* Structured attributes — shown as elegant pills */}
          {(() => {
            const attrs = [];
            if (therapist.age) attrs.push({ label: t("therapist.age"), value: `${therapist.age} ${t("therapist.years")}` });
            if (ml(therapist.nationality)) attrs.push({ label: t("therapist.nationality"), value: ml(therapist.nationality) });
            if (ml(therapist.hair)) {
              const eyeVal = ml(therapist.eyes);
              attrs.push({
                label: t("therapist.hair"),
                value: eyeVal
                  ? `${ml(therapist.hair)} · ${t("therapist.eyes")} ${eyeVal}`
                  : ml(therapist.hair),
              });
            }
            if (therapist.height) {
              const heightStr = String(therapist.height);
              attrs.push({
                label: t("therapist.height"),
                value: heightStr.includes("m") ? heightStr : `${heightStr} m`,
              });
            }
            if (therapist.weight) attrs.push({ label: t("therapist.weight"), value: String(therapist.weight) });
            if (attrs.length === 0) return null;
            return (
              <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {attrs.map((a) => (
                  <div key={a.label} className="rounded-xl border border-foreground/10 bg-foreground/[0.03] px-3 py-2.5">
                    <dt className="text-[10px] uppercase tracking-[0.25em] text-gold/85">{a.label}</dt>
                    <dd className="mt-0.5 text-sm text-foreground/85">{a.value}</dd>
                  </div>
                ))}
              </dl>
            );
          })()}

          {ml(therapist.about) && (
            <p className="mt-6 font-display text-xl font-light italic leading-relaxed text-foreground/82">
              {ml(therapist.about)}
            </p>
          )}

          <h3 className="mt-10 text-xs uppercase tracking-[0.3em] text-foreground/65">{t("therapist.specialties")}</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {specialties.map((m) => (
              <Link
                key={m.slug}
                data-testid={`therapist-specialty-${m.slug}`}
                to={`/massagens/${m.slug}`}
                className="rounded-full border border-foreground/15 px-4 py-2 text-sm text-foreground/82 transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                {ml(m.name)}
              </Link>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-gold/20 bg-gold/5 p-7">
            <h3 className="font-display text-2xl font-medium text-foreground">
              {t("therapist.bookWith")} {therapist.name}
            </h3>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="therapist-book-button"
                onClick={() => openBooking({ therapistSlug: therapist.slug })}
                className="flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-all duration-300 hover:bg-gold-light"
              >
                {t("nav.book")}
              </button>
              <a
                data-testid="therapist-availability-link"
                href={waLink(availabilityText)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-foreground/20 px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-foreground transition-all duration-300 hover:border-gold hover:text-gold"
              >
                <WhatsAppIcon className="h-4 w-4" />
                {t("therapist.requestAvailability")}
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      {therapist.gallery?.length > 0 && (
        <div className="mt-24">
          <Reveal>
            <h2 className="font-display text-4xl font-light text-foreground sm:text-5xl">{t("therapist.gallery")}</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
            {therapist.gallery.map((img, i) => (
              <Reveal key={img} delay={(i % 4) * 0.08}>
                <button
                  type="button"
                  data-testid={`gallery-photo-${i}`}
                  onClick={() => setLightbox(i)}
                  className="group block w-full cursor-zoom-in overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/[0.03] transition-colors duration-300 hover:border-gold/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <img
                    src={imgUrl(img)}
                    alt={`${therapist.name} ${i + 1}`}
                    loading="lazy"
                    className="aspect-[3/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      {lightbox !== null && therapist.gallery?.[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          onTouchStart={(e) => {
            touchX.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 50 && galleryLen > 1) {
              setLightbox((v) => (dx < 0 ? (v + 1) % galleryLen : (v - 1 + galleryLen) % galleryLen));
            }
          }}
        >
          {/* Top bar: counter + (mobile arrows) + close */}
          <div className="flex items-center justify-between px-4 pt-4 sm:px-5 sm:pt-5">
            <span className="text-xs tracking-[0.3em] text-white/90">
              {lightbox + 1} / {galleryLen}
            </span>
            <div className="flex items-center gap-2.5">
              {galleryLen > 1 && (
                <>
                  <button
                    type="button"
                    data-testid="lightbox-prev-top"
                    aria-label="Previous"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox((v) => (v - 1 + galleryLen) % galleryLen);
                    }}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-black/60 text-[#D4AF37] transition-colors active:bg-[#D4AF37] active:text-black sm:hidden"
                  >
                    <ChevronLeft className="h-5 w-5" strokeWidth={1.8} />
                  </button>
                  <button
                    type="button"
                    data-testid="lightbox-next-top"
                    aria-label="Next"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightbox((v) => (v + 1) % galleryLen);
                    }}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-black/60 text-[#D4AF37] transition-colors active:bg-[#D4AF37] active:text-black sm:hidden"
                  >
                    <ChevronRight className="h-5 w-5" strokeWidth={1.8} />
                  </button>
                </>
              )}
              <button
                type="button"
                data-testid="lightbox-close"
                aria-label="Close"
                onClick={() => setLightbox(null)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/60 text-white transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          {/* Main image with side arrows (desktop) */}
          <div className="relative flex min-h-0 flex-1 items-center justify-center px-0 py-2 sm:px-20 sm:py-3">
            {galleryLen > 1 && (
              <>
                <button
                  type="button"
                  data-testid="lightbox-prev"
                  aria-label="Previous"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox((v) => (v - 1 + galleryLen) % galleryLen);
                  }}
                  className="absolute left-2 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-black/70 text-[#D4AF37] shadow-lg transition-all hover:scale-105 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black sm:left-5 sm:flex sm:h-14 sm:w-14"
                >
                  <ChevronLeft className="h-6 w-6" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  data-testid="lightbox-next"
                  aria-label="Next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightbox((v) => (v + 1) % galleryLen);
                  }}
                  className="absolute right-2 top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-black/70 text-[#D4AF37] shadow-lg transition-all hover:scale-105 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black sm:right-5 sm:flex sm:h-14 sm:w-14"
                >
                  <ChevronRight className="h-6 w-6" strokeWidth={1.8} />
                </button>
              </>
            )}
            <img
              src={imgUrl(therapist.gallery[lightbox])}
              alt={`${therapist.name} ${lightbox + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="h-full w-full select-none object-contain sm:h-auto sm:max-h-full sm:w-auto sm:max-w-full sm:rounded-xl sm:border sm:border-white/10"
              draggable={false}
            />
          </div>

          {/* Thumbnail filmstrip */}
          {galleryLen > 1 && (
            <div
              data-testid="lightbox-filmstrip"
              className="flex justify-start gap-2.5 overflow-x-auto px-5 pb-5 pt-2 sm:justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {therapist.gallery.map((img, i) => (
                <button
                  key={img}
                  type="button"
                  data-testid={`lightbox-thumb-${i}`}
                  aria-label={`Photo ${i + 1}`}
                  ref={i === lightbox ? activeThumbRef : null}
                  onClick={() => setLightbox(i)}
                  className={`relative h-20 w-14 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-300 sm:h-24 sm:w-[68px] ${
                    i === lightbox
                      ? "border-[#D4AF37] opacity-100 shadow-[0_0_14px_rgba(212,175,55,0.35)]"
                      : "border-transparent opacity-45 hover:opacity-80"
                  }`}
                >
                  <img src={imgUrl(img)} alt="" loading="lazy" className="h-full w-full object-cover" draggable={false} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cross navigation */}
      <div className="mt-24">
        <Reveal>
          <h2 className="font-display text-3xl font-light text-foreground sm:text-4xl">{t("explore.otherTherapists")}</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-3 gap-4 sm:grid-cols-5">
          {others.map((th, i) => (
            <Reveal key={th.slug} delay={(i % 5) * 0.06}>
              <Link
                data-testid={`related-therapist-${th.slug}`}
                to={`/terapeutas/${th.slug}`}
                className="group block text-center"
              >
                <div className="overflow-hidden rounded-xl border border-foreground/10 transition-colors duration-300 group-hover:border-gold/50">
                  <img
                    src={imgUrl(th.profile)}
                    alt={th.name}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <span className="mt-2 block text-sm text-foreground/82 transition-colors duration-300 group-hover:text-gold">
                  {th.name}
                </span>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 flex items-center justify-between gap-4 border-t border-border pt-6">
          <Link
            data-testid="therapist-prev-link"
            to={`/terapeutas/${prevT.slug}`}
            className="group flex items-center gap-3 text-foreground/72 transition-colors hover:text-gold"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
            <span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-foreground/55">{t("explore.previous")}</span>
              <span className="font-display text-lg">{prevT.name}</span>
            </span>
          </Link>
          <Link
            data-testid="therapist-next-link"
            to={`/terapeutas/${nextT.slug}`}
            className="group flex items-center gap-3 text-right text-foreground/72 transition-colors hover:text-gold"
          >
            <span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-foreground/55">{t("explore.next")}</span>
              <span className="font-display text-lg">{nextT.name}</span>
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}
